import { LoadingState, Alert } from '../ui';
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { Button as FcButton, Card as FcCard, LinkButton as FcLinkButton } from '../ui';

export default function ReferralLanding() {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    checkSession();
    fetchReferrerInfo();
  }, [referralCode]);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setSession(session);
  };

  const fetchReferrerInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch referrer profile info via user_referral_codes table
      const { data, error } = await supabase
        .from("user_referral_codes")
        .select("user_id, referral_code, profiles!inner(username, created_at)")
        .eq("referral_code", referralCode)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setError("Invalid referral link. This code may not exist.");
        } else {
          throw error;
        }
        return;
      }

      // Flatten the joined profiles data
      const referrerData = {
        username: data.profiles.username,
        created_at: data.profiles.created_at
      };
      setReferrer(referrerData);
    } catch (err) {
      setError("Failed to load referral details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = () => {
    // Store referral code for signup process
    sessionStorage.setItem("pendingReferral", referralCode);
    navigate(`/signup?ref=${referralCode}`);
  };

  const handleLogin = () => {
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas">
        <LoadingState label="Loading invitation..." />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SidebarNavbar session={session} />
        <div role="main" id="fc-main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas p-4 md:ml-64 pb-20 fc-workspace">
          <FcCard as="div" padding="standard" className="bg-surface-card p-8 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-ui-green mb-4">Oops!</h1>
            <Alert tone="danger">{error}</Alert>
            <FcLinkButton variant="primary"
              to="/signup"
              className="inline-block"
            >
              Sign Up Anyway
            </FcLinkButton>
          </FcCard>
        </div>
        <BottomNavbar session={session} />
      </>
    );
  }

  if (!referrer) {
    return null;
  }

  if (session) {
    // User is already logged in
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas p-4">
        <FcCard as="div" padding="standard" className="bg-surface-card p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-5xl mb-4">👋</div>
          <h1 className="text-2xl font-bold text-ui-green mb-4">
            You're Already a Member!
          </h1>
          <p className="text-content-primary mb-6">
            You're already signed up with Footy Coach. Head to your dashboard to start competing!
          </p>
          <FcLinkButton variant="primary"
            to="/dashboard"
            className="inline-block w-full"
          >
            Go to Dashboard
          </FcLinkButton>
        </FcCard>
      </div>
    );
  }

  return (
    <>
      <SidebarNavbar session={session} />
      <div role="main" id="fc-main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas p-4 md:ml-64 pb-20 fc-workspace">
        <FcCard as="div" padding="standard" className="bg-surface-card p-8 rounded-lg shadow-lg max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-ui-green mb-2">
            You're Invited to Footy Coach!
          </h1>
          <p className="text-lg text-action-primary font-semibold">
            {referrer.username} wants you to join
          </p>
        </div>

        {/* Benefits Section */}
        <div className="bg-surface-canvas rounded-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-ui-green mb-4 text-center">
            Join & Get Rewards! 🎟️
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">✅</div>
              <div>
                <p className="font-semibold text-ui-green">Earn Free Tickets</p>
                <p className="text-sm text-content-secondary">Get bonus tickets when you sign up with this link</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="text-2xl">🏆</div>
              <div>
                <p className="font-semibold text-ui-green">Join Competitions</p>
                <p className="text-sm text-content-secondary">Compete in season-long leagues and knockout tournaments</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="text-2xl">🔍</div>
              <div>
                <p className="font-semibold text-ui-green">Scout Mode</p>
                <p className="text-sm text-content-secondary">Predict exact scores each matchweek</p>
              </div>
            </div>
            
          </div>
        </div>

        {/* Call to Action */}
        <FcButton variant="primary" type="submit"
          onClick={handleSignup}
          className="w-full mb-3"
        >
          Sign Up with Bonus Tickets
        </FcButton>
        
        <FcButton variant="secondary" type="submit"
          onClick={handleLogin}
          className="w-full border-2"
        >
          Already have an account? Login
        </FcButton>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-content-secondary">
            By signing up, you agree to our{" "}
            <Link to="/terms" className="text-action-primary hover:underline">
              Terms
            </Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-action-primary hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>
        </FcCard>
      </div>
      <BottomNavbar session={session} />
    </>
  );
}
